import { ColDef, ValueFormatterParams, IRowNode } from '@ag-grid-community/core';
import { Icon } from '@creditsights/styleguide-web';
import { NavLink } from 'react-router-dom';
import { EM_DASH } from '../../../../Base.constants';
import { IssuerListItemDto } from '../../../../services/dtos/issuer/issuerList.dto';
import { getIssuerRoute } from '../../../../utils/navigationUtils';
import { WEIGHTING_ICONS_MAP } from '../../../Companies/Company.constants';
import {
  generateCoverageString,
  generateCSViewString,
  impliedCreditValueGetter,
  impliedValueGetter,
} from '../../../Companies/Companies.logic';
import Coverage from '../../../Companies/Coverage';

interface ColDefWithMandatoryId extends ColDef<IssuerListItemDto> {
  colId: string;
}

export const WATCHLIST_TABLE_COL_PERSISTENCE_KEY = 'watchlist-table-columns';

export const WATCHLIST_TABLE_PAGE_SIZE = 150;

export const DISPLAY_NAME_COL_ID = 'name';
export const TICKER_COL_ID = 'ticker';

export const WATCHLIST_TABLE_COL_DEFS: ColDefWithMandatoryId[] = [
  {
    valueFormatter: () => '',
    colId: 'selection',
    lockPosition: 'left',
    pinned: 'left',
    checkboxSelection: true,
    resizable: false,
    suppressMovable: true,
    maxWidth: 24,
    headerCheckboxSelection: true,
    headerClass: 'selection-cell',
    cellClass: 'selection-cell',
    sortable: false,
  },
  {
    colId: DISPLAY_NAME_COL_ID,
    lockPosition: 'left',
    field: 'displayName',
    headerName: 'Issuer',
    pinned: 'left',
    cellRenderer: ({ data }: ValueFormatterParams<IssuerListItemDto>) => (
      <NavLink to={getIssuerRoute(data!.tagId)}>{data!.displayName}</NavLink>
    ),
  },
  {
    colId: TICKER_COL_ID,
    field: 'displayTicker',
    headerName: 'Ticker/Identifier',
  },
  {
    colId: 'latestRecommendation.weighting',
    headerName: 'CS View',
    valueGetter: (params) => (params.data?.csCoverage ? generateCSViewString(params) : null),
    cellRenderer: ({ data, value }: ValueFormatterParams<IssuerListItemDto>) =>
      value ? (
        <Icon name={WEIGHTING_ICONS_MAP[data!.latestRec!.weighting.toLowerCase()]} height={20} width={20} />
      ) : (
        EM_DASH
      ),
    comparator: csViewValueComparator,
  },
  {
    colId: 'sector',
    headerName: 'Sector',
    valueGetter: impliedValueGetter('sector'),
  },
  {
    colId: 'topLevelRegion',
    headerName: 'Region',
    valueGetter: impliedValueGetter('region'),
  },
  {
    colId: 'creditQualities',
    headerName: 'Credit Grade',
    valueGetter: impliedCreditValueGetter,
  },
  {
    colId: 'coverage',
    headerName: 'Coverage',
    cellRenderer: ({ data }: ValueFormatterParams<IssuerListItemDto>) => (
      <Coverage
        csCoverage={data!.csCoverage}
        crCoverage={data!.crCoverage}
        lfiCoverage={data!.lfiCoverage}
        financialsCoverage={data!.financialsCoverage}
        riskProductsCoverage={data!.riskProductsCoverage}
      />
    ),
    suppressSizeToFit: true,
    minWidth: 80,
    width: 130,
    valueGetter: (params) => generateCoverageString(params),
    sortable: false,
  },
];

function csViewValueComparator(
  valueA: string,
  valueB: string,
  nodeA: IRowNode<IssuerListItemDto>,
  nodeB: IRowNode<IssuerListItemDto>,
  isDescending: boolean
): number {
  if (!valueA) {
    return isDescending ? -1 : 1;
  }
  if (!valueB) {
    return isDescending ? 1 : -1;
  }
  return 0;
}
